// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import systemZIndex from "@steiner/XDL/PreStyle/zIndex";
import isDate from "lodash/isDate";

interface GetScriptsOptions {
    appendTo?: Element;
    id?: string;
    async?: boolean;
}

/**
 * To load JS script with callback.
 * you can pass one url or multiple
 * Example:
 * getScripts.urls([ // url1, url2]).then(() => { //callback handler });
 */
export const getScripts = {
    url: (url: string, options?: GetScriptsOptions) => {
        return new Promise((resolve, reject) => {
            if (typeof document !== "undefined") {
                const script = document.createElement("script");
                script.type = "text/javascript";
                script.src = url;
                Object.keys(options || {}).forEach(key => {
                    if (key in (options || {})) {
                        (script as any)[key] = (options as any)[key];
                    }
                });

                script.addEventListener("load", () => resolve(script), false);
                script.addEventListener(
                    "error",
                    () => {
                        reject(script);
                        document.body.removeChild(script);
                    },
                    false,
                );

                if (options?.appendTo) {
                    options.appendTo.appendChild(script);
                } else {
                    document.body.appendChild(script);
                }
            }
        });
    },
    urls: (urls: string[], options?: GetScriptsOptions) => {
        return Promise.all(urls.map(url => getScripts.url(url, options)));
    },
};

export const isSmallMobileScreen = () =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 480px)").matches;

export const isMobileScreen = () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches;

export const isTabletScreen = () => typeof window !== "undefined" && window.matchMedia("(max-width: 1024px)").matches;

export const isWideScreen = () => typeof window !== "undefined" && window.matchMedia("(min-width: 2200px)").matches;

/**
 * To convert string to boolean
 * @param value
 */
export const parseBoolean = (value?: string) => (value ? value?.toLowerCase() === "true" : false);

/**
 * split without empty or undefined values
 * @param value
 */
export const flatSplit = (value?: string, target = "") => (value || "").split(target).filter(item => item) || [];

/**
 * Method use to pass for props not accept undefined
 */
export const doNothing = () => {};

export const isNonEmptyString = (value: string | undefined) => value !== undefined && value.trim() !== "";

export const exportToCSV = async (fields: string[], data: (string | number | null)[][], filename?: string) => {
    const dataForUnparse = {
        fields,
        data,
    };

    const { unparse } = await import(/* webpackChunkName: "papaparse" */ "papaparse");
    const csv = unparse(dataForUnparse);
    const csvBlob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8;" });
    const fileName = `${filename || "csv_exported_file"}_${Math.random() * 10000}.csv`;
    const downloadLink = document.createElement("a");
    downloadLink.href = window.URL.createObjectURL(csvBlob);
    downloadLink.setAttribute("download", fileName);
    downloadLink.click();
};

export const getSystemZIndex = (key: string): number => {
    const zIndexValues = systemZIndex as any;
    return zIndexValues[key] || -1;
};

export const formatDate = (date: Date | null) => {
    if (!date) {
        return "";
    }

    let dt = date;
    if (!isDate(dt)) {
        dt = new Date(dt);
    }

    return getLocalizedDateTime({
        dateTime: dt,
        options: {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
        },
    });
};

export const getVideoEmbeddedUrl = (src: string) => {
    if (!src) {
        return;
    }
    if (src.includes("vimeo")) {
        return src;
    }
    const srcParts = src.split("/");
    const videoId = srcParts.pop();
    return `https://www.youtube.com/embed/${videoId}`;
};

export const getYoutubeThumbnail = (url: string) => {
    // This regex matches all youtube urls ( embed, shortlinks, full-url )
    if (!url) {
        return "";
    }
    const match = url.match(
        /^((?:https?:)?\/\/)?((?:www|m)\.)?((?:youtube(-nocookie)?\.com|youtu.be))(\/(?:[\w\-]+\?v=|shorts\/|embed\/|v\/)?)([\w\-]+)(\S+)?$/,
    );

    if (!match?.length) {
        return "";
    }
    const videoId = match[6];
    return `https://img.youtube.com/vi/${videoId}/default.jpg`;
};

export const getImageSize = (image: string): Promise<{ width: number; height: number }> => {
    return new Promise((resolve, reject) => {
        try {
            const img = new Image();

            img.onload = () => {
                resolve({ width: img.width, height: img.height });
            };

            img.src = image;
        } catch (e) {
            reject(e);
        }
    });
};

/**
 * Set a CSS variable on the `:root` element dynamically by injecting a `<style>` element.
 * If the new value is the same as the current value, no update is performed to avoid unnecessary DOM changes.
 *
 * @param {string} variableName - The name of the CSS variable to update (e.g., `--header-height`).
 * @param {string} value - The new value to assign to the CSS variable (e.g., `100px`).
 *
 * @example
 * updateCSSVariable('--header-height', '100px');
 */
export const setCSSVariable = (variableName: string, value: string) => {
    if (typeof document === "undefined") {
        return;
    }
    // Get the current value of the CSS variable
    const currentValue = getComputedStyle(document.documentElement).getPropertyValue(variableName).trim();

    // Check if the new value is different from the current value
    if (currentValue === value) {
        return;
    }

    // Create or find the <style> element
    let styleElement = document.getElementById("app-styles") as HTMLStyleElement;
    if (!styleElement) {
        styleElement = document.createElement("style");
        styleElement.id = "app-styles";
        document.head.appendChild(styleElement);
    }

    // Update the CSS rule for :root
    styleElement.textContent = `:root { ${variableName}: ${value}; }`;
};

export const getCategoryLevels = (properties?: { [key: string]: string }) => {
    const result: { [key: string]: string } = {};

    const mainCategory = properties?.["category"] || "";
    const categories = properties?.["categories"] || "";

    const categoryList = categories
        .split(",")
        .map(c => c.trim())
        .filter(c => c !== mainCategory);

    result["item_category"] = mainCategory;

    categoryList.forEach((category, index) => {
        result[`item_category${index + 2}`] = category;
    });

    return result;
};

/**
 * Changes the stroke color of an SVG encoded in base64.
 * @param {string} svgBase64 - The base64-encoded SVG string.
 * @param {string} color - The new color to apply to the stroke (can be in hex, rgba, or named color format).
 * @returns {string} - The new base64-encoded SVG string with the updated color.
 */
export const changeSvgColor = (svgBase64: string, color: string) => {
    // Step 1: Decode the base64 string
    const decodedSvg = atob(svgBase64);

    // Step 2: Modify the stroke color in the SVG (replace stroke="currentColor" with the new color)
    const updatedSvg = decodedSvg.replace(/stroke="([^"]*)"/, `stroke="${color}"`);

    // Step 3: Re-encode the modified SVG to base64
    const encodedSvg = btoa(updatedSvg);

    return encodedSvg;
};